<!--
  会话侧边栏组件。
  负责展示、搜索、置顶、删除和切换会话，是聊天页左侧的会话入口。
-->
<template>
  <div ref="sessionSidebarRef" class="session-sidebar" @scroll="handleSessionListScroll">
    <h3>会话列表({{ displaySessionList.length }})</h3>
    <el-button class="new-chat" type="primary" :icon="CirclePlus" @click="handleNewChat">新建会话</el-button>

    <div>
      <!-- 搜索模式 -->
      <div v-if="isSearchMode" class="search-container">
        <el-input v-model="searchKeyword" placeholder="搜索会话" :prefix-icon="Search" @keyup.enter="handleSearch"
          class="search-input">
          <template #suffix>
            <el-icon class="clear-icon" @click="handleClearSearch">
              <Close />
            </el-icon>
          </template>
        </el-input>
      </div>

      <!-- 普通模式 - 搜索按钮 -->
      <el-button v-else class="search-btn" :icon="Search" @click="handleToggleSearch">搜索会话</el-button>
    </div>

    <div ref="sessionListContainerRef" class="session-list-container" @scroll="handleSessionListScroll">
      <ul class="session-list">
        <template v-if="shouldShowSessionSkeleton">
          <li v-for="index in sessionSkeletonCount" :key="`session-skeleton-${index}`"
            class="session-item session-skeleton">
            <div class="session-content">
              <div class="skeleton-line skeleton-title"></div>
              <div class="session-info">
                <div class="skeleton-line skeleton-id"></div>
                <div class="skeleton-line skeleton-count"></div>
              </div>
            </div>
          </li>
        </template>
        <li v-for="session in displaySessionList" :key="session.id" :class="{ active: session.id === currentSessionId }"
          @click="handleSwitchSession(session.id)" class="session-item">
          <div class="session-content">
            <div class="session-title">
              <!-- 置顶图标 -->
              <el-button v-if="session.isPinned" type="success" :icon="Top" circle size="small" class="more-btn" />
              <span :title="session.title">{{ session.title }}</span>
            </div>
            <div class="session-info">
              <div class="session-id" :title="session.id">ID: {{ session.id }}</div>
              <div class="message-count">{{ session.messageCount }} 条消息</div>
            </div>
          </div>
          <div class="session-actions" @click.stop>
            <el-dropdown trigger="hover" @command="(command) => handleCommand(command, session)">
              <!-- 三个点 -->
              <el-button type="info" :icon="MoreFilled" circle size="small" class="more-btn" />
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="top">
                    <el-icon>
                      <Top />
                    </el-icon>
                    {{ session.isPinned ? '取消置顶' : '置顶' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="rename">
                    <el-icon>
                      <Edit />
                    </el-icon>
                    重命名
                  </el-dropdown-item>
                  <el-dropdown-item command="share">
                    <el-icon>
                      <Share />
                    </el-icon>
                    分享
                  </el-dropdown-item>
                  <el-dropdown-item command="download-md">
                    <el-icon>
                      <Download />
                    </el-icon>
                    下载 Markdown
                  </el-dropdown-item>
                  <el-dropdown-item command="download-json">
                    <el-icon>
                      <Download />
                    </el-icon>
                    下载 JSON
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <!-- <el-popconfirm title="确定删除此会话吗？" confirm-button-text="确定" cancel-button-text="取消"
                      @confirm="handleDeleteSession(session.id, $event)">
                      <template #reference>
                        <el-button type="danger" :icon="Delete" circle size="small" class="delete-btn" />
                      </template>
          </el-popconfirm> -->
                    <el-icon>
                      <Delete color="#f56c6c" />
                    </el-icon>
                    <span style="color: #f56c6c;">删除</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </li>
        <li v-if="displaySessionList.length === 0 && !isLoading && hasLoadedSessionList" class="empty-session">
          {{ isSearchMode ? '未找到匹配的会话' : '暂无会话记录' }}
        </li>
      </ul>
    </div>

    <!-- 重命名对话框 s -->
    <el-dialog v-model="renameDialogVisible" title="重命名会话" width="400px">
      <el-input v-model="newTitle" placeholder="请输入新的会话标题" maxlength="20" show-word-limit />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="renameDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleConfirmRename" :loading="isRenaming"
            :disabled="!newTitle">确定</el-button>
        </span>
      </template>
    </el-dialog>
    <!-- 重命名对话框 e -->

    <!-- 分享对话框 -->
    <ShareDialog v-model="shareDialogVisible" :session="currentShareSession" :user-id="currentUserId" />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import { CirclePlus, Delete, Search, Close, MoreFilled, Edit, Share, Download, Top } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useSessionList, type SessionInfo } from '~/composables/useSessionList'
import { ChatRole, StorageKey } from '~/enums'
import ShareDialog from '~/components/ShareDialog.vue'

// Props
interface Props {
  currentSessionId: string
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'switch-session': [sessionId: string]
  'new-chat': []
  'delete-session': [sessionId: string]
}>()

const sessionSidebarRef = ref<HTMLElement | null>(null)
const sessionListContainerRef = ref<HTMLElement | null>(null)

// ==================== Composables ====================

const {
  currentUserId,
  sessionList,
  isLoading,
  lastSessionListLoadedAt,
  loadSessionList,
  removeSessionFromList,
  refreshSessionList
} = useSessionList()

const hasLoadedSessionList = computed(() => lastSessionListLoadedAt.value > 0)
const sessionSkeletonCount = 7
const shouldShowSessionSkeleton = computed(() => isLoading.value && displaySessionList.value.length === 0)

// ==================== 搜索相关状态 ====================

// 服务端和客户端初始状态必须一致，避免 hydration mismatch
const isSearchMode = ref(false)
const searchKeyword = ref('') // 搜索关键字
const isMounted = ref(false)
let isRestoringSessionListScroll = false

// 从 cookie 恢复搜索状态（仅在客户端挂载后执行）
// 这样可以确保服务端渲染时始终是默认状态（搜索关闭）
const restoreSearchState = () => {
  try {
    // 延迟到客户端才恢复搜索状态，避免 hydration mismatch
    if (import.meta.client) {
      const savedMode = localStorage.getItem(StorageKey.SESSION_SEARCH_MODE)
      const savedKeyword = localStorage.getItem(StorageKey.SESSION_SEARCH_KEYWORD)
      if (savedMode === 'true') {
        isSearchMode.value = true
        searchKeyword.value = savedKeyword || ''
      }
    }
  } catch (error) {
    console.error('❌ 恢复搜索状态失败:', error)
  }
}

// 保存搜索状态到 localStorage
const saveSearchState = () => {
  if (!isMounted.value || !import.meta.client) {
    return
  }

  try {
    localStorage.setItem(StorageKey.SESSION_SEARCH_MODE, String(isSearchMode.value))
    localStorage.setItem(StorageKey.SESSION_SEARCH_KEYWORD, searchKeyword.value)
  } catch (error) {
    console.error('❌ 保存搜索状态失败:', error)
  }
}


const getSavedSessionListScrollTop = () => {
  if (!import.meta.client) {
    return 0
  }

  const scrollTop = Number(localStorage.getItem(StorageKey.SESSION_LIST_SCROLL_TOP))
  return Number.isFinite(scrollTop) && scrollTop >= 0 ? scrollTop : 0
}

const getSessionListScrollElements = () => [sessionSidebarRef.value, sessionListContainerRef.value]
  .filter((element): element is HTMLElement => Boolean(element))

const saveSessionListScrollTop = () => {
  if (!isMounted.value || !import.meta.client || isRestoringSessionListScroll) {
    return
  }

  const scrollTop = Math.max(...getSessionListScrollElements().map(element => element.scrollTop), 0)
  localStorage.setItem(StorageKey.SESSION_LIST_SCROLL_TOP, String(scrollTop))
}

const restoreSessionListScrollTop = async () => {
  if (!import.meta.client) {
    return
  }

  await nextTick()
  const scrollTop = getSavedSessionListScrollTop()
  const restore = () => {
    getSessionListScrollElements().forEach(element => {
      element.scrollTop = scrollTop
    })
  }

  isRestoringSessionListScroll = true
  restore()
  requestAnimationFrame(() => {
    restore()
    isRestoringSessionListScroll = false
  })
}

const handleSessionListScroll = () => {
  saveSessionListScrollTop()
}
// 监听搜索状态变化并自动保存
watch([isSearchMode, searchKeyword], () => {
  saveSearchState()
})

// 计算显示的会话列表（根据搜索条件过滤）
const displaySessionList = computed(() => {
  if (!isSearchMode.value || !searchKeyword.value.trim()) {
    return sessionList.value
  }

  const keyword = searchKeyword.value.toLowerCase().trim()
  return sessionList.value.filter(session =>
    session.title.toLowerCase().includes(keyword) ||
    session.id.toLowerCase().includes(keyword)
  )
})

// ==================== 重命名相关状态 ====================

const renameDialogVisible = ref(false) // 重命名对话框是否可见
const newTitle = ref('') // 新的会话标题
const currentRenameSession = ref<SessionInfo | null>(null) // 当前要重命名的会话信息
const isRenaming = ref(false) // 重命名操作是否正在进行

// ==================== 分享相关状态 ====================
const shareDialogVisible = ref(false)
const currentShareSession = ref<SessionInfo | null>(null)

// ==================== 方法 ====================
/**
 * 切换搜索模式
 */
const handleToggleSearch = () => {
  isSearchMode.value = true
  searchKeyword.value = ''
  // 聚焦到输入框
  nextTick(() => {
    const input = document.querySelector('.search-input input')
    if (input) {
      (input as HTMLInputElement).focus()
    }
  })
}

/**
 * 执行搜索（按回车键）
 */
const handleSearch = () => {
  // 搜索逻辑已通过 computed 自动处理
  if (!searchKeyword.value.trim()) {
    ElMessage.warning('请输入搜索关键词')
  }
}

/**
 * 清除搜索，恢复列表显示
 */
const handleClearSearch = () => {
  isSearchMode.value = false
  searchKeyword.value = ''
  // 清除 localStorage 中的搜索状态
  try {
    localStorage.removeItem(StorageKey.SESSION_SEARCH_MODE)
    localStorage.removeItem(StorageKey.SESSION_SEARCH_KEYWORD)
  } catch (error) {
    console.error('❌ 清除搜索状态失败:', error)
  }
}

/**
 * 切换会话
 * @param sessionId 
 */
const handleSwitchSession = (sessionId: string) => {
  // 如果点击的是当前会话，不做任何操作
  if (sessionId === props.currentSessionId) {
    return
  }
  saveSessionListScrollTop()
  emit('switch-session', sessionId)
}

/**
 * 点击【新会话】按钮，创建新会话。
 */
const handleNewChat = () => {
  emit('new-chat')
}

/**
 * 处理下拉菜单命令
 * @param command 命令类型
 * @param session 会话信息
 */
const handleCommand = (command: string, session: SessionInfo) => {
  switch (command) {
    case 'top':
      handleTop(session)
      break
    case 'share':
      handleShare(session)
      break
    case 'download-md':
      handleDownload(session, 'md')
      break
    case 'download-json':
      handleDownload(session, 'json')
      break
    case 'rename':
      handleRename(session)
      break
    case 'delete':
      handleDeleteSession(session.id, new Event('click'))
      break
  }
}

/**
 * 置顶/取消置顶会话
 * @param session 
 */
const handleTop = async (session: SessionInfo) => {
  if (!currentUserId.value) {
    ElMessage.error('用户未登录')
    return
  }

  try {
    // 切换置顶状态
    const newPinnedState = !session.isPinned

    // 🔗调用接口：PATCH /api/session/pin
    const response: any = await $fetch('/api/session/pin', {
      method: 'PATCH',
      body: {
        sessionId: session.id,
        userId: currentUserId.value,
        isPinned: newPinnedState
      }
    })

    if (response.success) {
      ElMessage.success(response.message || (newPinnedState ? '置顶成功' : '取消置顶成功'))
      // 重新加载会话列表
      await refreshSessionList()
    } else {
      ElMessage.error(response.message || '操作失败')
    }
  } catch (error) {
    console.error('❌ 切换置顶状态失败:', error)
    ElMessage.error('操作失败')
  }
}

/**
 * 分享会话
 * @param session
 */
const handleShare = (session: SessionInfo) => {
  if (session.messageCount === 0) {
    ElMessage.warning('空会话无法分享')
    return
  }
  currentShareSession.value = session
  shareDialogVisible.value = true
}

/**
 * 下载会话内容
 * @param session 会话信息
 * @param format 下载格式 ('md' | 'json')
 */
const handleDownload = async (session: SessionInfo, format: 'md' | 'json') => {
  if (!currentUserId.value) {
    ElMessage.error('用户未登录')
    return
  }

  if (session.messageCount === 0) {
    ElMessage.warning('空会话无法下载')
    return
  }

  try {
    // 获取会话消息
    const response: any = await $fetch('/api/message/messages', {
      method: 'GET',
      query: {
        sessionId: session.id,
        userId: currentUserId.value
      }
    })

    if (!response.success || !response.messages) {
      ElMessage.error('获取会话消息失败')
      return
    }

    const messages = response.messages
    let content: string
    let filename: string
    let mimeType: string

    if (format === 'md') {
      // 导出为 Markdown
      content = convertToMarkdown(session, messages)
      filename = `${session.title || '会话'}.md`
      mimeType = 'text/markdown'
    } else {
      // 导出为 JSON
      const exportData = {
        title: session.title,
        sessionId: session.id,
        exportedAt: new Date().toLocaleString('zh-CN'),
        messages: messages.map((msg: any) => ({
          role: msg.role,
          content: msg.content,
          thinkingSteps: msg.thinkingSteps || [],
          attachments: msg.attachments || [],
          createdAt: msg.createdAt ? new Date(msg.createdAt).toLocaleString('zh-CN') : ''
        }))
      }
      content = JSON.stringify(exportData, null, 2)
      filename = `${session.title || '会话'}.json`
      mimeType = 'application/json'
    }

    // 创建 Blob 并触发下载
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    ElMessage.success(`下载成功：${filename}`)
  } catch (error) {
    console.error('下载失败:', error)
    ElMessage.error('下载失败')
  }
}

/**
 * 将消息转换为 Markdown 格式
 */
const convertToMarkdown = (session: SessionInfo, messages: any[]): string => {
  let md = `# ${session.title || '会话记录'}\n\n`
  md += `> 导出时间：${new Date().toLocaleString('zh-CN')}\n\n`
  md += `---\n\n`
  let roundNum = 0

  for (const msg of messages) {
    const isUser = msg.role === ChatRole.USER
    const isAI = msg.role === ChatRole.ASSISTANT
    const time = msg.createdAt ? new Date(msg.createdAt).toLocaleString('zh-CN') : ''

    if (isUser) {
      roundNum += 1
      md += `## 第 ${roundNum} 轮对话\n\n`
      md += `### 💬 用户提问\n\n`
      md += `${formatMarkdownQuote(msg.content)}\n\n`

    } else if (isAI) {
      md += `### 💡 AI 回复\n\n`
      md += `${shiftMarkdownHeadings(msg.content)}\n\n`

      if (msg.thinkingSteps && msg.thinkingSteps.length > 0) {
        md += `<details>\n`
        md += `<summary>回复思考过程（${msg.thinkingSteps.length} 步）</summary>\n\n`
        md += `| 步骤 | 状态 | 内容 |\n`
        md += `|:---:|:---:|:---|\n`
        for (const step of msg.thinkingSteps) {
          const stepText = step.text || step.content || String(step)
          const statusIcon = step.status === 'completed' ? '✅' : step.status === 'pending' ? '⏳' : step.status === 'error' ? '❌' : '🔄'
          md += `| ${escapeMarkdownTableCell(step.icon || '•')} | ${statusIcon} ${escapeMarkdownTableCell(step.status || '')} | ${escapeMarkdownTableCell(stepText)} |\n`
        }
        md += `\n</details>\n\n`
      }
    }

    if (time) {
      md += `> 消息时间：${time}\n\n`
    }

    // 如果有附件
    if (msg.attachments && msg.attachments.length > 0) {
      md += `\n📎 附件：\n`
      for (const att of msg.attachments) {
        md += `- ${att.name} (${att.type}, ${formatFileSize(att.size)})\n`
      }
      md += `\n`
    }

    md += `---\n\n`
  }

  return md
}

/**
 * 格式化 Markdown 引用
 */
const formatMarkdownQuote = (content: string): string => {
  const normalizedContent = content || '（空内容）'
  return normalizedContent
    .split('\n')
    .map(line => `> ${line}`)
    .join('\n')
}

/**
 * 移除 Markdown 标题
 */
const shiftMarkdownHeadings = (content: string): string => {
  const normalizedContent = content || '（空内容）'
  let isInCodeBlock = false

  return normalizedContent
    .split('\n')
    .map(line => {
      if (/^\s*```/.test(line) || /^\s*~~~/.test(line)) {
        isInCodeBlock = !isInCodeBlock
        return line
      }

      if (isInCodeBlock || line.trimStart().startsWith('>')) {
        return line
      }

      return line.replace(/^(#{1,5})(\s+)/, '#$1$2')
    })
    .join('\n')
}


/**
 * 转义 Markdown 表格单元格内容
 * @param value 
 */
const escapeMarkdownTableCell = (value: string): string => {
  return String(value)
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, '<br>')
}

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
/**
 * 打开重命名对话框
 * @param session 要重命名的会话
 */
const handleRename = (session: SessionInfo) => {
  currentRenameSession.value = session // 保存当前会话信息到状态中
  newTitle.value = session.title
  renameDialogVisible.value = true
}

/**
 * 确认重命名
 */
const handleConfirmRename = async () => {
  if (!currentRenameSession.value) return

  const trimmedTitle = newTitle.value.trim()
  if (!trimmedTitle) {
    ElMessage.warning('请输入会话标题')
    return
  }

  isRenaming.value = true
  try {
    // 🔗调用接口：PATCH /api/session/title
    const response: any = await $fetch('/api/session/title', {
      method: 'PATCH',
      body: {
        sessionId: currentRenameSession.value.id,
        title: trimmedTitle,
        userId: currentUserId.value
      }
    })

    if (response.success) {
      ElMessage.success('重命名成功')
      renameDialogVisible.value = false
      // 重新加载会话列表
      await refreshSessionList()
    } else {
      ElMessage.error(response.message || '重命名失败')
    }
  } catch (error) {
    console.error('❌ 重命名失败:', error)
    ElMessage.error('重命名失败')
  } finally {
    isRenaming.value = false
  }
}

/**
 * 点击【删除】会话
 * @param sessionId  
 * @param event 
 */
const handleDeleteSession = (sessionId: string, event: Event) => {
  event.stopPropagation()

  ElMessageBox.confirm(
    '此操作将永久删除您创建的所有聊天记录，且无法撤销。请确认以继续。',
    '删除对话记录',
    {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
    }
  )
    .then(async () => {
      try {
        // 🔗调用接口：POST /api/session/session-delete
        const response: any = await $fetch(`/api/session/session-delete`, {
          method: 'POST',
          body: {
            sessionId,
            userId: currentUserId.value
          }
        })

        if (response.success) {
          ElMessage.success('会话删除成功')
          removeSessionFromList(sessionId)
          emit('delete-session', sessionId)
          // 重新加载会话列表
          await refreshSessionList()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        console.error('❌ 删除会话失败:', error)
        ElMessage.error('删除会话失败')
      }
    })
    .catch(() => {
    })
}

// 暴露给父组件
defineExpose({
  loadSessionList,
  refreshSessionList
})

// 组件挂载时加载会话列表并恢复侧边栏状态
onMounted(async () => {
  isMounted.value = true
  restoreSearchState()
  await loadSessionList()
  await restoreSessionListScrollTop()
})

onBeforeUnmount(() => {
  saveSessionListScrollTop()
})
</script>

<style scoped lang="scss">
@use "./SessionSidebar.scss"
</style>
